import { AppDispatch, AppGetState } from '@/redux/store';
import { fetchBankAccountsIfNeeded } from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import { fetchBidderCreditCardsIfNeeded } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.actions';
import { fetchBidderDataIfNeeded } from '@/redux/modules/account/user/user.actions';
import { fetchCatalogDetailsIfNeeded } from '@/redux/modules/catalog/detail/catalogDetail.actions';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { fetchInvoice } from '@/redux/modules/checkout/checkout.actions';
import { fetchItemDetailsIfNeeded } from '@/redux/modules/itemDetail';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchProcessingFeesIfNeeded } from '@/redux/modules/processingFee';
import { fetchSellersIfNeeded } from '@/redux/modules/seller';
import { fetchSimilarItemsIfNeeded } from '@/redux/modules/similarItems';
import { fetchTaxStatus } from '@/redux/modules/taxes';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getInvoiceById } from '@/redux/modules/checkout/checkout.selectors';
import { getItemSummarySellerId, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { loadCheckoutShippingQuote } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.actions';
import { sleep } from '@/utils/sleep';

// this is used directly in the CheckoutPageContainer as it needs to be run client side not SSR
export const getCheckoutPageData =
    (catalogId: number, forceUserData: boolean) => async (dispatch: AppDispatch, getState: AppGetState) => {
        const authToken = getAuthToken(getState());

        if (Boolean(authToken)) {
            return Promise.all([
                dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] })),
                dispatch(fetchCatalogDetailsIfNeeded([catalogId])),
                dispatch(fetchBidderDataIfNeeded({ authToken, force: forceUserData })),
            ])
                .then(() => {
                    return Promise.all([
                        dispatch(fetchInvoice(catalogId)),
                        dispatch(fetchBankAccountsIfNeeded()),
                        dispatch(fetchBidderCreditCardsIfNeeded()),
                    ]);
                })
                .then(() => {
                    const { invoice, items } = getInvoiceById(getState(), catalogId);
                    const { id: invoiceId } = invoice;

                    if (items.length) {
                        const { itemId: maybeStringItemId, sellerId } = items[0];
                        const itemId = Number(maybeStringItemId);
                        const promises = [
                            dispatch(
                                fetchItemSummariesIfNeeded({
                                    identifier: 'CHECKOUT-PAGE-DATA-LOADER',
                                    itemIds: [Number(itemId)],
                                })
                            ),
                            dispatch(fetchItemDetailsIfNeeded([Number(itemId)])),
                            dispatch(fetchItemFacetsIfNeeded([Number(itemId)])),
                            dispatch(fetchSellersIfNeeded([Number(sellerId)])),
                            dispatch(fetchProcessingFeesIfNeeded(catalogId)),
                            dispatch(fetchTaxStatus(invoiceId)),
                            dispatch(loadCheckoutShippingQuote(invoiceId)),
                        ];

                        return Promise.all(promises).then(() => {
                            const sellerId = getItemSummarySellerId(getState(), itemId);
                            const title = getItemSummaryTitle(getState(), itemId);
                            const facets = getItemFacets(getState(), itemId);
                            const categoryIds = getFacetIds(facets);
                            const similarItemInfo = {
                                categoryIds,
                                itemId,
                                sellerId,
                                title,
                            };

                            return Promise.all([dispatch(fetchSimilarItemsIfNeeded(similarItemInfo))]);
                        });
                    }

                    return Promise.resolve({});
                });
        } else {
            //sometimes this call happens before hydrate client readies the auth token.
            return Promise.all([sleep(500)]).then(() => {
                getCheckoutPageData(catalogId, forceUserData);
            });
        }
    };
